import { useEffect, useReducer } from 'react'

/** How often time-dependent UI (e.g. "Possibly stalled") is re-evaluated. */
export const CLOCK_TICK_MS = 30_000

/**
 * The current time, re-evaluated every `intervalMs` while enabled.
 *
 * Some state depends on the clock rather than on server data (a RUNNING run
 * becomes "stalled" after 20 minutes without any response changing), so
 * nothing would otherwise re-render when it flips. Pass `null` to stop ticking
 * when nothing on screen depends on it.
 *
 * The returned value is always read fresh at render time; the interval only
 * forces a re-render, so a paused clock can never hand back a stale time.
 */
export function useNow(intervalMs: number | null): number {
  const [, forceRender] = useReducer((tick: number) => tick + 1, 0)

  useEffect(() => {
    if (intervalMs === null) return undefined
    const id = setInterval(forceRender, intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])

  return Date.now()
}
